import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TitleSection from "../components/TitleSection/TitleSection";
import Button from "../components/Button";
import ContactFormPages from "../components/ContactFormPages";
import { Images } from "../assets";

const Machines = () => {
  const { t } = useTranslation();
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch("/api/category");
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    const fetchMachines = async () => {
      try {
        const response = await fetch("/api/machine");
        const data = await response.json();
        setMachines(data);
      } catch (error) {
        console.error("Error fetching machines:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMachines();
    fetchCategories();
  }, []);

  const toggleFilter = (filter) => {
    setSelectedFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter],
    );
  };

  const filteredMachines =
    selectedFilters.length === 0
      ? machines
      : machines.filter((machine) =>
          machine.categories.some((category) =>
            selectedFilters.includes(category.name),
          ),
        );

  const Filter = ({ name }) => (
    <div
      className="flex items-center gap-3 border-b border-orange py-3 pl-3 cursor-pointer"
      onClick={() => toggleFilter(name)}
    >
      <label className="relative flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={selectedFilters.includes(name)}
          onChange={() => toggleFilter(name)}
          className="peer appearance-none w-8 h-8 bg-white rounded checked:bg-orange"
          style={{ boxShadow: "inset 0 4px 6px rgba(0, 0, 0, 0.3)" }}
        />
        <img
          src={Images.WHITE_TICK}
          alt="Tick"
          className="absolute w-8 h-8 opacity-0 peer-checked:opacity-100 transition-opacity duration-200 left-0 top-0"
        />
      </label>
      <p className="text-xl">{name}</p>
    </div>
  );

  const Machine = ({ name, pictures, id }) => (
    <div className="relative flex flex-col items-center justify-center gap-5 bg-grey p-10">
      {(pictures[0]?.type ?? "").includes("video/") ? (
        <video
          src={pictures[0]?.url}
          alt={name}
        />
      ) : (
        <img
          src={pictures[0]?.url}
          alt={name}
        />
      )}
      <h3 className="text-black text-xl text-center line-clamp-2">{name}</h3>
      <div className="absolute bottom-[-1rem]">
        <NavLink to={`/machine/${id}`}>
          <Button
            text={t("machines.button")}
            px="px-3"
            py="py-2"
            orientation="south-east"
          />
        </NavLink>
      </div>
    </div>
  );

  return (
    <>
      <TitleSection
        title={t("machines.title")}
        subtitle={t("machines.description")}
        highlightWords={[t("machines.highlight")]}
        bgImg={Images.MACHINES_BG_IMAGE}
      />

      <section className="flex flex-col md:flex-row items-start gap-5 mx-5 md:mx-10 my-10">
        {/* Filter Sidebar acting as a dropdown */}
        <div className="w-full md:w-1/3 flex flex-col gap-3">
          <Button
            text={t("machines.filterButton")}
            width="100%"
            px="px-3"
            py="py-3"
            textSize="text-xl"
            orientation={showFilters ? "north" : "south"}
            onClick={() => setShowFilters(!showFilters)}
          />

          <AnimatePresence>
            {showFilters ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                {categories.map((category) => (
                  <Filter key={category.id} name={category.name} />
                ))}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        {/* Machine Grid (Filtered) */}
        <div className="w-full md:w-2/3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 gap-y-20">
          {loading ? (
            <p className="col-span-full text-center text-xl text-gray-500">
              {t("loading")}
            </p>
          ) : filteredMachines.length > 0 ? (
            filteredMachines.map((machine, index) => (
              <Machine key={index} {...machine} />
            ))
          ) : (
            <p className="col-span-full text-center text-xl text-gray-500">
              {t("machines.noMachinesFound")}
            </p>
          )}
        </div>
      </section>

      <ContactFormPages />
    </>
  );
};

export default Machines;
